require("./threepl-train-vouchers-view.scss");

import {ChangeDetectionStrategy, Component, OnInit, inject, input, signal} from "@angular/core";
import {toSignal} from "@angular/core/rxjs-interop";
import {Observable} from "rxjs";
import * as _ from "lodash";
import {BringozDateRange} from "../../../../commons/angular/models/bringoz-date-range.model";
import {TrainVoucher, TrainVouchersFilters} from "../../models/train-voucher.model";
import {ThreePLTrainHttpService} from "../../services/threepl-train-http.service";
import {ThreePLTrainService} from "../../services/threepl-train.service";
import {ThreePLTrainVouchersStore} from "../../state/threepl-train-vouchers.store";
import {UiRouterStateService} from "../../../../commons/angular/models/ui-router-state.model";
import {DateRangeTimeFiltersService} from "../../../../commons/services/date-range-time-filters.service";
import {STATE_URL_ONLY_OPTIONS} from "../../constants/threepl.constant";

@Component({
  selector: "app-threepl-train-vouchers-view",
  templateUrl: "./threepl-train-vouchers-view.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ThreePLTrainVouchersStore],
})
export class ThreeplTrainVouchersViewComponent implements OnInit {
  readonly from = input<number>();
  readonly to = input<number>();
  readonly voucherId = input<string>();
  readonly serviceId = input<string>();

  readonly store = inject(ThreePLTrainVouchersStore);
  readonly vm = toSignal(this.store.vm$, {requireSync: true});

  readonly filters = signal<TrainVouchersFilters | undefined>(undefined);
  readonly editingVoucher = signal<TrainVoucher | undefined>(undefined);
  readonly openingVoucher = signal(false);

  private readonly dateRangeTimeFiltersService = inject<DateRangeTimeFiltersService>("dateRangeTimeFiltersService" as never);
  private readonly state = inject<UiRouterStateService>("$state" as never);
  private readonly trainService = inject(ThreePLTrainService);
  private readonly http = inject(ThreePLTrainHttpService);

  ngOnInit(): void {
    const from = this.from();
    const to = this.to();
    this.filters.set({
      customTemplates: this.dateRangeTimeFiltersService.getDeliveriesCustomTemplates(),
      range: from && to ? {dateStart: from, dateEnd: to} : this.dateRangeTimeFiltersService.thisMonth(),
      active: true,
    });
    this.query();
    this.openInitialVoucher();
  }

  onRangeChanged(range: BringozDateRange): void {
    this.filters.update((filters) => ({...filters, range}));
    this.query();
    this.syncUrl();
  }

  onEditVoucher(voucher: TrainVoucher): void {
    this.editingVoucher.set(_.cloneDeep(voucher));
    this.syncUrl();
  }

  onBack(): void {
    this.editingVoucher.set(undefined);
    this.syncUrl();
  }

  onUpdateVoucher(voucher: TrainVoucher): void {
    this.trainService.updateService(this.editingVoucher().id, voucher)
      .then(() => {
        this.editingVoucher.set(undefined);
        this.query();
        this.syncUrl();
      })
      .catch(() => undefined);
  }

  onCompleteVoucher(voucher: TrainVoucher): void {
    this.trainService.completeService(voucher.id).then(() => this.query()).catch(() => undefined);
  }

  onCancelVoucher(voucher: TrainVoucher): void {
    this.trainService.cancelService(voucher.id).then(() => this.query()).catch(() => undefined);
  }

  onPrintVoucher(voucher: TrainVoucher): void {
    this.trainService.printVoucher([voucher.id]);
  }

  onFetchNext(): void {
    this.store.loadMore();
  }

  private query(): void {
    const {range, active} = this.filters();
    this.store.load({from: range.dateStart, to: range.dateEnd, active});
  }

  private syncUrl(): void {
    const {range} = this.filters();
    this.state.go(".", {voucherId: this.editingVoucher()?.id, from: range.dateStart, to: range.dateEnd}, STATE_URL_ONLY_OPTIONS);
  }

  private openInitialVoucher(): void {
    const serviceId = this.serviceId();
    const voucherId = this.voucherId();
    if (!serviceId && !voucherId) {
      return;
    }
    const request: Observable<TrainVoucher> = serviceId ? this.http.getThirdPartyService(serviceId) : this.http.getOne(voucherId);
    this.openingVoucher.set(true);
    request.subscribe({
      next: (voucher) => {
        this.openingVoucher.set(false);
        if (!voucher) {
          return;
        }
        this.editingVoucher.set(_.cloneDeep(voucher));
        if (serviceId) {
          this.state.go(".", {voucherId: voucher.id, serviceId: undefined, from: this.from(), to: this.to()}, STATE_URL_ONLY_OPTIONS);
        }
      },
      error: () => this.openingVoucher.set(false),
    });
  }
}
